import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2 } from '@/components/icons';
import { useBuildings } from '@/features/buildings/api/use-buildings';
import { MAX_HITS_PER_GROUP, type CommandEntityHit } from './types';

/**
 * Building entity-search adapter (W129-F5-S2).
 *
 * Reads the already-cached `useBuildings()` result (5-min staleTime) and
 * filters client-side by case-insensitive substring on `name`. No refetch
 * on keystroke — typing in the palette is free.
 *
 * When `query` is empty we return [] rather than the full list, so an
 * accidental palette open doesn't dump every building. Static navigation
 * actions ("Go to buildings") cover the "I want to browse" case.
 *
 * Up to 5 hits — the palette renders 3 groups, so a 15-row palette stays
 * scannable. `BuildingListItem.name` is the canonical display field; the
 * partner name (when stitched in by the apps/api proxy) is the sublabel.
 */
export function useBuildingsCommandHits(query: string): readonly CommandEntityHit[] {
  const buildingsQuery = useBuildings();
  const navigate = useNavigate();

  return useMemo<readonly CommandEntityHit[]>(() => {
    const trimmed = query.trim();
    if (!trimmed) return [];
    const buildings = buildingsQuery.data ?? [];
    if (buildings.length === 0) return [];

    const needle = trimmed.toLowerCase();
    const matched = buildings.filter((b) =>
      (b.name ?? '').toLowerCase().includes(needle),
    );
    return matched.slice(0, MAX_HITS_PER_GROUP).map<CommandEntityHit>((b) => ({
      id: `building:${b.id}`,
      label: b.name ?? b.id,
      sublabel: b.partner_name ?? undefined,
      kind: 'buildings',
      icon: Building2,
      onSelect: () => navigate(`/buildings/${b.id}`),
    }));
  }, [query, buildingsQuery.data, navigate]);
}
